import { ArrowPathIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import ROUTES from '@/ROUTES'

import { api, api_delete } from '@/services/api.service'

import ConfirmModal from '@/components/ConfirmModal'

import { useProviderSession } from './ProviderSession'
import { LicenseStatus } from './types'

interface ProviderLicense {
    _id: string
    email: string
    status: LicenseStatus
    consentToShareDetail: boolean
    registered: boolean
    invitedAt?: string
    activatedAt?: string
    lastActiveAt?: string
}

interface ProviderLicenseListResponse {
    licenses: ProviderLicense[]
    quota: { used: number; total: number }
}

interface InviteReport {
    invited: string[]
    reinvited: string[]
    skippedExisting: string[]
    skippedQuota: string[]
    invalid: string[]
    seatsUsed: number
    seatQuota: number
}

const STATUS_LABEL: Record<LicenseStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
    invited: { label: 'Invited', cls: 'bg-amber-100 text-amber-800' },
    expired: { label: 'Expired', cls: 'bg-neutral-200 text-neutral-700' },
    revoked: { label: 'Revoked', cls: 'bg-red-100 text-red-700' },
}

const FILTERS: { id: 'all' | LicenseStatus; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'invited', label: 'Invited' },
    { id: 'expired', label: 'Expired' },
    { id: 'revoked', label: 'Revoked' },
]

function formatDate(value?: string) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ProviderLicenses() {
    const session = useProviderSession()
    // Admins and coordinators can invite; resend/revoke are admin-only; the
    // viewer role is read-only everywhere.
    const canInvite = session?.role === 'admin' || session?.role === 'coordinator'
    const isAdmin = session?.role === 'admin'
    const [data, setData] = useState<ProviderLicenseListResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState<'all' | LicenseStatus>('all')
    const [showAssign, setShowAssign] = useState(false)
    const [revokeId, setRevokeId] = useState<string | null>(null)
    const [pendingActionId, setPendingActionId] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    async function refresh() {
        setLoading(true)
        const response = await api<ProviderLicenseListResponse>(ROUTES.provider.license)
        if (response.status && response.status < 400 && response.data) {
            setData(response.data)
        }
        setLoading(false)
    }

    useEffect(() => {
        refresh()
    }, [])

    const licenses = data?.licenses ?? []
    const filtered = filter === 'all' ? licenses : licenses.filter((l) => l.status === filter)
    const seatsUsed = data?.quota.used ?? 0
    const seatQuota = data?.quota.total ?? 0
    const seatsRemaining = Math.max(0, seatQuota - seatsUsed)

    async function handleResend(licenseId: string) {
        setActionError(null)
        setPendingActionId(licenseId)
        const response = await api(`${ROUTES.provider.license}/${licenseId}/resend`, {})
        setPendingActionId(null)
        if (response.status && response.status >= 400) {
            setActionError(response.error || 'Failed to resend invite')
            return
        }
        await refresh()
    }

    async function handleRevoke(licenseId: string) {
        setActionError(null)
        setPendingActionId(licenseId)
        const response = await api(`${ROUTES.provider.license}/${licenseId}/revoke`, {})
        setPendingActionId(null)
        setRevokeId(null)
        if (response.status && response.status >= 400) {
            setActionError(response.error || 'Failed to revoke license')
            return
        }
        await refresh()
    }

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Licenses</h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        {seatsUsed} of {seatQuota} seats assigned.{' '}
                        <span className='text-neutral-900 font-medium'>{seatsRemaining} remaining.</span>
                    </p>
                </div>
                {canInvite && (
                    <div className='flex items-center gap-2'>
                        <button
                            onClick={() => setShowAssign(true)}
                            className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors'
                        >
                            <PlusIcon className='w-4 h-4' />
                            Assign license
                        </button>
                    </div>
                )}
            </div>

            {actionError && (
                <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800'>
                    {actionError}
                </div>
            )}

            {/* Filter tabs */}
            <div className='flex items-center gap-1 mb-4 border-b border-neutral-200 overflow-x-auto'>
                {FILTERS.map((f) => {
                    const count = f.id === 'all' ? licenses.length : licenses.filter((l) => l.status === f.id).length
                    const isActive = filter === f.id
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                                isActive
                                    ? 'border-brand-600 text-brand-700'
                                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                            }`}
                        >
                            {f.label} <span className='text-neutral-400 ml-1'>({count})</span>
                        </button>
                    )
                })}
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Email</th>
                                <th className='px-4 py-3 text-left font-medium'>Status</th>
                                <th className='px-4 py-3 text-left font-medium'>Invited</th>
                                <th className='px-4 py-3 text-left font-medium'>Last active</th>
                                <th className='px-4 py-3 text-right font-medium'>Actions</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {filtered.map((lic) => (
                                <tr key={lic._id} className='hover:bg-neutral-50/50'>
                                    <td className='px-4 py-3 text-neutral-900'>{lic.email}</td>
                                    <td className='px-4 py-3'>
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_LABEL[lic.status].cls}`}
                                        >
                                            {STATUS_LABEL[lic.status].label}
                                        </span>
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>{formatDate(lic.invitedAt) ?? '—'}</td>
                                    <td className='px-4 py-3 text-neutral-600'>{formatDate(lic.lastActiveAt) ?? 'Never'}</td>
                                    <td className='px-4 py-3'>
                                        <div className='flex items-center justify-end gap-1'>
                                            {isAdmin && lic.status === 'invited' && (
                                                <button
                                                    title='Resend invite'
                                                    disabled={pendingActionId === lic._id}
                                                    onClick={() => handleResend(lic._id)}
                                                    className='p-1.5 text-neutral-500 hover:text-brand-600 hover:bg-brand-50 rounded disabled:opacity-50'
                                                >
                                                    <ArrowPathIcon className='w-4 h-4' />
                                                </button>
                                            )}
                                            {isAdmin && lic.status !== 'revoked' && (
                                                <button
                                                    title='Revoke'
                                                    disabled={pendingActionId === lic._id}
                                                    onClick={() => setRevokeId(lic._id)}
                                                    className='p-1.5 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50'
                                                >
                                                    <TrashIcon className='w-4 h-4' />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!loading && filtered.length === 0 && (
                    <div className='py-12 text-center text-sm text-neutral-500'>
                        {licenses.length === 0
                            ? 'No licenses yet. Assign one to invite a family.'
                            : 'No licenses match this filter.'}
                    </div>
                )}
                {loading && (
                    <div className='py-12 text-center text-sm text-neutral-500'>Loading…</div>
                )}
            </div>

            {showAssign && (
                <AssignLicenseModal
                    seatsRemaining={seatsRemaining}
                    seatQuota={seatQuota}
                    onClose={() => setShowAssign(false)}
                    onSent={async () => {
                        await refresh()
                    }}
                />
            )}

            <ConfirmModal
                open={!!revokeId}
                onCancel={() => setRevokeId(null)}
                onConfirm={() => revokeId && handleRevoke(revokeId)}
                title='Revoke license'
                description={
                    licenses.find((l) => l._id === revokeId)?.status === 'active'
                        ? 'Revoking frees the seat. The family loses sponsored access and will receive an email letting them know their sponsorship has ended. You can re-invite the same email later, which will consume a seat again.'
                        : 'Revoking frees the seat and quietly withdraws the invitation; no email is sent. You can re-invite the same email later, which will consume a seat again.'
                }
                confirmText='Revoke'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}

function AssignLicenseModal({
    seatsRemaining,
    seatQuota,
    onClose,
    onSent,
}: {
    seatsRemaining: number
    seatQuota: number
    onClose: () => void
    onSent: () => Promise<void>
}) {
    const session = useProviderSession()
    const [text, setText] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [report, setReport] = useState<InviteReport | null>(null)

    function parseEmails(raw: string): string[] {
        return raw.split(/[\r\n,;]+/).map((s) => s.trim()).filter(Boolean)
    }

    async function handleSubmit() {
        setError(null)
        setReport(null)
        const emails = parseEmails(text)
        if (emails.length === 0) {
            setError('Enter at least one email address')
            return
        }
        setSubmitting(true)
        // suppressForbiddenRedirect: a role-denied 403 should show inline in the
        // modal, not bounce the admin to /forbidden mid-task.
        const response = await api<InviteReport>(ROUTES.provider.licenseInvite, { emails }, undefined, undefined, true)
        setSubmitting(false)
        if (response.status && response.status >= 400) {
            setError(response.error || 'Failed to send invitations')
            return
        }
        setReport(response.data ?? null)
        await onSent()
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50' onClick={onClose}>
            <div className='bg-white rounded-2xl shadow-xl w-full max-w-md p-6' onClick={(e) => e.stopPropagation()}>
                <div className='flex items-center justify-between mb-4'>
                    <h2 className='text-lg font-semibold text-neutral-900'>Assign a license</h2>
                    <button onClick={onClose} className='p-1 text-neutral-400 hover:text-neutral-700'>
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>

                {report ? (
                    <InviteReportView report={report} onDone={onClose} />
                ) : (
                    <>
                        <p className='text-sm text-neutral-600 mb-4'>
                            Enter one or more email addresses. Each family receives an invitation email; when they
                            register with that email, their license activates automatically.
                            {session?.role === 'coordinator' && ' Families you invite are added to your caseload.'}
                        </p>

                        <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Email addresses</label>
                        <textarea
                            rows={5}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={'parent1@example.com\nparent2@example.com'}
                            className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500'
                        />
                        <p className='text-xs text-neutral-500 mt-1'>One email per line, comma-separated, or paste a list.</p>

                        <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900'>
                            Each new invitation uses 1 seat. {seatsRemaining} seats remaining of {seatQuota}.
                        </div>

                        {error && (
                            <div className='mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800'>
                                {error}
                            </div>
                        )}

                        <div className='flex items-center justify-end gap-2 mt-6'>
                            <button onClick={onClose} className='px-4 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900'>
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className='px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg'
                            >
                                {submitting ? 'Sending…' : 'Send invitations'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

function InviteReportView({ report, onDone }: { report: InviteReport; onDone: () => void }) {
    const sections: { label: string; emails: string[]; cls: string }[] = [
        { label: 'Invited', emails: report.invited, cls: 'text-green-800' },
        { label: 'Re-invited (previously revoked)', emails: report.reinvited, cls: 'text-green-800' },
        { label: 'Already a coordinator/family on this org — skipped', emails: report.skippedExisting, cls: 'text-amber-800' },
        { label: 'Skipped: over seat quota', emails: report.skippedQuota, cls: 'text-amber-800' },
        { label: 'Invalid emails', emails: report.invalid, cls: 'text-red-800' },
    ].filter((s) => s.emails.length > 0)

    return (
        <>
            <p className='text-sm text-neutral-600 mb-4'>
                {report.seatsUsed} of {report.seatQuota} seats now in use.
            </p>
            <div className='space-y-3 max-h-72 overflow-y-auto'>
                {sections.length === 0 && (
                    <div className='p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700'>
                        No emails were processed.
                    </div>
                )}
                {sections.map((s) => (
                    <div key={s.label}>
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${s.cls}`}>
                            {s.label} ({s.emails.length})
                        </div>
                        <ul className='text-sm text-neutral-700 space-y-0.5'>
                            {s.emails.map((e) => (
                                <li key={e} className='font-mono'>{e}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className='flex items-center justify-end gap-2 mt-6'>
                <button
                    onClick={onDone}
                    className='px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                >
                    Done
                </button>
            </div>
        </>
    )
}
